// A single-value inline editor for instrument text (adapted from the corpus
// LineEditor, ADR-006 pattern): contentEditable rendering real formatting, a
// floating B/I/U toolbar over the selection (plus Cmd/Ctrl-B/I/U), serialised
// back to the shared **bold** / *italic* / _underline_ string form on blur.
// Single block: Enter is swallowed; instrument fields have no line breaks.

import { useLayoutEffect, useRef, useState, type JSX } from 'react';
import { tokenizeInlineMarkup, tokensToMarkup, type InlineMarkupToken } from '@cca/parishes-shared';
import { escapeHtml } from '@cca/safe';

function tokensToHtml(value: string): string {
  return tokenizeInlineMarkup(value)
    .map((t) => {
      let html = escapeHtml(t.text);
      if (t.bold) html = `<b>${html}</b>`;
      if (t.italic) html = `<i>${html}</i>`;
      if (t.underline) html = `<u>${html}</u>`;
      return html;
    })
    .join('');
}

type Fmt = { bold?: boolean; italic?: boolean; underline?: boolean };

const isBold = (el: HTMLElement): boolean => {
  const t = el.tagName.toLowerCase();
  if (t === 'b' || t === 'strong') return true;
  const w = el.style.fontWeight;
  return w === 'bold' || (!!w && Number(w) >= 600);
};
const isItalic = (el: HTMLElement): boolean => {
  const t = el.tagName.toLowerCase();
  return t === 'i' || t === 'em' || el.style.fontStyle === 'italic';
};
const isUnderline = (el: HTMLElement): boolean => {
  const t = el.tagName.toLowerCase();
  return t === 'u' || el.style.textDecoration.includes('underline');
};

/** Merge adjacent tokens with identical marks so the stored string stays tidy. */
function mergeTokens(tokens: InlineMarkupToken[]): InlineMarkupToken[] {
  const out: InlineMarkupToken[] = [];
  for (const t of tokens) {
    const prev = out[out.length - 1];
    if (
      prev &&
      !!prev.bold === !!t.bold &&
      !!prev.italic === !!t.italic &&
      !!prev.underline === !!t.underline
    ) {
      prev.text += t.text;
    } else {
      out.push({ ...t });
    }
  }
  return out;
}

/** Serialise the contenteditable back to the markup string. Block boundaries
 *  and <br> (from pastes) flatten to spaces — instrument text is one line. */
function elementToMarkup(root: HTMLElement): string {
  const tokens: InlineMarkupToken[] = [];
  const walk = (node: Node, fmt: Fmt): void => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        const text = child.textContent ?? '';
        if (text) tokens.push({ text, ...fmt });
        return;
      }
      if (child.nodeType !== Node.ELEMENT_NODE) return;
      const el = child as HTMLElement;
      const tag = el.tagName.toLowerCase();
      if (tag === 'br') {
        tokens.push({ text: ' ' });
        return;
      }
      const next: Fmt = { ...fmt };
      if (isBold(el)) next.bold = true;
      if (isItalic(el)) next.italic = true;
      if (isUnderline(el)) next.underline = true;
      if (tag === 'div' || tag === 'p') tokens.push({ text: ' ' });
      walk(el, next);
    });
  };
  walk(root, {});
  return tokensToMarkup(mergeTokens(tokens)).replace(/\s+/g, ' ').trim();
}

export function InlineMarkupEditor({
  value,
  onSave,
  placeholder,
  className,
}: {
  value: string;
  onSave: (next: string) => void;
  placeholder?: string;
  className?: string;
}): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const [bar, setBar] = useState<{ top: number; left: number } | null>(null);

  // Seed the editable from the model when the external value changes; never
  // while this field owns focus (that would stomp the caret mid-edit).
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && document.activeElement !== el) el.innerHTML = tokensToHtml(value);
  }, [value]);

  const save = (): void => {
    setBar(null);
    const el = ref.current;
    if (!el) return;
    const next = elementToMarkup(el);
    if (next !== value) onSave(next);
    else el.innerHTML = tokensToHtml(value); // tidy stray empty wrappers
  };

  const refreshBar = (): void => {
    const sel = window.getSelection();
    const root = ref.current;
    if (!sel || sel.isCollapsed || sel.rangeCount === 0 || !root) {
      setBar(null);
      return;
    }
    const range = sel.getRangeAt(0);
    if (!root.contains(range.commonAncestorContainer)) {
      setBar(null);
      return;
    }
    const r = range.getBoundingClientRect();
    setBar({ top: r.top - 40, left: r.left + r.width / 2 });
  };

  const apply = (cmd: 'bold' | 'italic' | 'underline'): void => {
    // execCommand is deprecated but remains the pragmatic, well-supported way
    // to toggle marks on a contenteditable selection (corpus precedent).
    document.execCommand(cmd);
    ref.current?.focus();
    refreshBar();
  };

  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      ref.current?.blur();
      return;
    }
    if ((e.metaKey || e.ctrlKey) && (e.key === 'b' || e.key === 'i' || e.key === 'u')) {
      e.preventDefault();
      apply(e.key === 'b' ? 'bold' : e.key === 'i' ? 'italic' : 'underline');
    }
  };

  return (
    <>
      <div
        ref={ref}
        className={`instr-edit${className ? ` ${className}` : ''}`}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        tabIndex={0}
        aria-multiline="false"
        data-placeholder={placeholder ?? ''}
        onKeyDown={onKeyDown}
        onKeyUp={refreshBar}
        onMouseUp={refreshBar}
        onBlur={save}
      />
      {bar && (
        <div className="instr-fmt-bar" style={{ top: bar.top, left: bar.left }}>
          {/* preventDefault keeps the selection while clicking the buttons */}
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => apply('bold')}
          >
            <b>B</b>
          </button>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => apply('italic')}
          >
            <i>I</i>
          </button>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => apply('underline')}
          >
            <u>U</u>
          </button>
        </div>
      )}
    </>
  );
}
